<template>
  <div class="project-geodesists-handbook">
    <PageTitle hideMarginLeft>
      Типы проектов и группы работ для вызова геодезиста
    </PageTitle>
    <p class="tw-text-grey-7 tw-mt-2 tw-mb-4 tw-max-w-2xl">
      Справочник связей типа проекта с группой работ: для указанных пар перед контролем по группе работ первым вызывается геодезист.
    </p>
    <div class="tw-pt-4">
      <div class="filters row q-gutter-md q-mb-md">
        <div class="filter-item">
          <CustomSelect
            label="Тип проекта"
            placeholder="Все типы"
            clearable
            :options="projectTypesOptions"
            option-value-key="project_type_id"
            option-label-key="project_type_name"
            :value="filters.project_type_id"
            @change="(val) => onFilterChange('project_type_id', val)"
          />
        </div>
        <div class="filter-item">
          <CustomSelect
            label="Группа работ"
            placeholder="Все группы"
            clearable
            :options="workSetCheckGroupsOptions"
            option-value-key="work_set_check_group_id"
            option-label-key="work_set_check_group_name"
            :value="filters.work_set_check_group_id"
            @change="(val) => onFilterChange('work_set_check_group_id', val)"
          />
        </div>
      </div>

      <q-linear-progress
        v-if="isProjectTypesLoading || isGroupsLoading"
        indeterminate
        class="tw-mb-4"
      />

      <div v-else class="table-wrapper">
        <CustomTable
          title="Связи «тип проекта — группа работ»"
          :rowsData="rowsData"
          :columns="projectGeodesistsColumns"
          :loading="isFetching"
          :show-search="false"
          :custom-body="true"
          :add-btn="true"
          :on-add-click="onAddClick"
        >
          <template #top-right>
            <CustomIcon
              icon="refresh"
              title="Обновить"
              :loading="isFetching"
              class="tw-mr-2"
              @click="loadData"
            />
          </template>
          <template #customBody="props">
            <q-tr>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.project_type_name }}
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.work_set_check_group_name }}
              </custom-table-cell>
              <custom-table-cell
                align="center"
                vertical-align="center"
                style="width: 100px; min-width: 100px;"
              >
                <div class="tw-flex tw-justify-center tw-items-center tw-gap-2">
                  <CustomIcon
                    icon="trash"
                    title="Удалить"
                    :approve-mode="true"
                    :approve="() => onDeleteClick(props.row)"
                  />
                </div>
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
        <NotFoundText v-if="!isFetching && rowsData.length === 0" />
      </div>
    </div>
  </div>
</template>

<script setup>
import PageTitle from "@/components/common/PageTitle.vue";
import CustomSelect from "@/components/form/CustomSelect";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import NotFoundText from "@/components/common/NotFoundText";
import { useQuery } from "@tanstack/vue-query";
import { ref, reactive, watch } from "vue";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { MODAL_NAMES } from "@/constants";
import {
  getProjectTypes,
  getWorkSetCheckGroups,
  getProjectGeodesists,
  addProjectGeodesist,
  deleteProjectGeodesist,
  projectGeodesistsColumns,
} from "./services";

const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();

const filters = reactive({
  project_type_id: null,
  work_set_check_group_id: null,
});

const { data: projectTypesData, isLoading: isProjectTypesLoading } = useQuery({
  queryKey: ["projectTypesProjectGeodesists"],
  queryFn: getProjectTypes,
  refetchOnWindowFocus: false,
});

const { data: workSetCheckGroupsData, isLoading: isGroupsLoading } = useQuery({
  queryKey: ["workSetCheckGroupsForSbs"],
  queryFn: getWorkSetCheckGroups,
  refetchOnWindowFocus: false,
});

const projectTypesOptions = ref([]);
const workSetCheckGroupsOptions = ref([]);

watch(
  projectTypesData,
  (val) => {
    projectTypesOptions.value = Array.isArray(val) ? val : val?.data ?? [];
  },
  { immediate: true }
);
watch(
  workSetCheckGroupsData,
  (val) => {
    workSetCheckGroupsOptions.value = Array.isArray(val) ? val : val?.data ?? [];
  },
  { immediate: true }
);

const rowsData = ref([]);
const isFetching = ref(false);

async function loadData() {
  isFetching.value = true;
  const res = await getProjectGeodesists({
    project_type_id: filters.project_type_id ?? undefined,
    work_set_check_group_id: filters.work_set_check_group_id ?? undefined,
  });
  isFetching.value = false;
  rowsData.value = res ?? [];
}

watch(
  () => [filters.project_type_id, filters.work_set_check_group_id],
  () => loadData(),
  { immediate: true }
);

function onFilterChange(key, value) {
  filters[key] = value ?? null;
}

function onAddClick() {
  appStore.showModal(MODAL_NAMES.AddProjectGeodesist, {
    title: "Добавить связь",
    projectTypesOptions: projectTypesOptions.value,
    workSetCheckGroupsOptions: workSetCheckGroupsOptions.value,
    onSubmit: async (body) => {
      const res = await addProjectGeodesist(body);
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

async function onDeleteClick(row) {
  const res = await deleteProjectGeodesist(row.project_geodesist_id, {
    project_type_id: filters.project_type_id ?? undefined,
    work_set_check_group_id: filters.work_set_check_group_id ?? undefined,
  });
  if (!res) return;
  showSucceedSnackbar();
  await loadData();
}
</script>

<style lang="scss" scoped>
.project-geodesists-handbook {
  min-height: 400px;
}

.filters {
  .filter-item {
    min-width: 280px;
  }
}

.table-wrapper {
  padding: 0;
}
</style>
